import type { AxiosResponse } from 'axios';
import { axiosInstance } from '../App';
import { SECURED_API_BASE } from './apiConfig';
import type { ExportFormat } from '../components/novarium/ExportMenu';
import { ApiErrorResponse } from './apiError';
import {
    ScheduleHoursDTO,
    ServiceStatisticsBenchmarkDTO,
    ServiceStatisticsFilter,
    ServiceStatisticsGranularity,
    ServiceStatisticsHourlyDTO,
    ServiceStatisticsLongestWaitDTO,
    ServiceStatisticsMetricsDTO,
    ServiceStatisticsOccupancyDTO,
    ServiceStatisticsOptionsDTO,
    ServiceStatisticsPriorityDTO,
    ServiceStatisticsServiceDTO,
    ServiceStatisticsSummaryDTO,
    ServiceStatisticsTimelineRowDTO,
    WaitRankingDTO,
} from '../dto/ServiceStatisticsDTO';

const BASE = `${SECURED_API_BASE}/service-statistics`;

/** 403: el usuario no tiene la acción de ver las estadísticas de todos los profesionales. */
export const STATISTICS_ACCESS_DENIED = 'STATISTICS_ACCESS_DENIED';
/** 403: «Mi desempeño» con un usuario que no está asociado a un profesional. */
export const STATISTICS_PROFESSIONAL_REQUIRED = 'STATISTICS_PROFESSIONAL_REQUIRED';

/** Por qué el usuario no puede ver la pantalla; null si el error es de otra cosa (red, servidor, fechas). */
export type StatisticsAccessProblem = 'denied' | 'professionalRequired';

/** Traduce un error ya parseado con `parseApiError` a un problema de acceso, si lo es. */
export function statisticsAccessProblem(error: ApiErrorResponse): StatisticsAccessProblem | null {
    if (error.errorCode === STATISTICS_ACCESS_DENIED) return 'denied';
    if (error.errorCode === STATISTICS_PROFESSIONAL_REQUIRED) return 'professionalRequired';
    return null;
}

/**
 * Los indicadores nuevos (percentil 90 y distribución de la espera) pueden no venir si el backend todavía no los manda:
 * en ese caso valen cero y la pantalla no los dibuja.
 */
function withMetricDefaults(metrics: ServiceStatisticsMetricsDTO): ServiceStatisticsMetricsDTO {
    return {
        ...metrics,
        waitP90Minutes: metrics.waitP90Minutes ?? 0,
        waitUpTo15: metrics.waitUpTo15 ?? 0,
        waitFrom15To30: metrics.waitFrom15To30 ?? 0,
        waitFrom30To60: metrics.waitFrom30To60 ?? 0,
    };
}

/**
 * Servicios, agendas, especialidades, profesionales y códigos de atención del sitio para los selects (aunque no tengan
 * atenciones). Con `mine`, solo las agendas y especialidades del profesional del usuario. Cada especialidad, servicio y
 * profesional trae a qué otros se relaciona (tipos de agenda, especialidades, agendas), así que los filtros se acomodan
 * entre sí en el cliente con esta única respuesta (`filterOptions.ts`); si el backend todavía no manda esos campos, no
 * se acota por ellos. Si no manda `access` ni `professionals`, el usuario no puede ver a todos y no hay profesionales.
 */
export function getServiceStatisticsOptions(mine = false): Promise<ServiceStatisticsOptionsDTO> {
    return axiosInstance.get(`${BASE}/options`, { params: mine ? { mine: true } : undefined })
        .then(r => ({
            ...r.data,
            specialities: r.data.specialities ?? [],
            access: r.data.access ?? { canViewAll: false, ownProfessionalId: null },
            professionals: r.data.professionals ?? [],
        }));
}

/** Indicadores del período y del período anterior de igual largo. */
export function getServiceStatisticsSummary(filter: ServiceStatisticsFilter): Promise<ServiceStatisticsSummaryDTO> {
    return axiosInstance.get(`${BASE}/summary`, { params: filter }).then(r => ({
        ...r.data,
        current: withMetricDefaults(r.data.current),
        previous: withMetricDefaults(r.data.previous),
    }));
}

/** Evolución por día o por mes. Solo trae los períodos con atenciones. */
export function getServiceStatisticsTimeline(filter: ServiceStatisticsFilter, granularity: ServiceStatisticsGranularity):
    Promise<ServiceStatisticsTimelineRowDTO[]> {
    return axiosInstance.get(`${BASE}/timeline`, { params: { ...filter, granularity } }).then(r => r.data ?? []);
}

/** Atenciones por servicio, día de la semana y hora (alimenta la curva por hora, el mapa de calor y los peores horarios). */
export function getServiceStatisticsHourly(filter: ServiceStatisticsFilter): Promise<ServiceStatisticsHourlyDTO> {
    return axiosInstance.get(`${BASE}/hourly`, { params: filter }).then(r => r.data);
}

/**
 * Indicadores de cada servicio o agenda, con el período anterior. Con tipo, especialidad y código, pero sin el filtro de
 * servicio: la tabla sigue mostrando todos los del tipo y la especialidad para poder pasar de uno a otro.
 */
export function getServiceStatisticsServices(filter: ServiceStatisticsFilter): Promise<ServiceStatisticsServiceDTO[]> {
    return axiosInstance.get(`${BASE}/services`, { params: { ...filter, serviceId: undefined } }).then(r =>
        ((r.data ?? []) as ServiceStatisticsServiceDTO[]).map(service => ({
            ...service,
            current: withMetricDefaults(service.current),
            previous: service.previous ? withMetricDefaults(service.previous) : null,
        })));
}

/** Atenciones y espera por código de atención. Sin el filtro de código: la lista muestra todos los códigos. */
export function getServiceStatisticsPriorities(filter: ServiceStatisticsFilter): Promise<ServiceStatisticsPriorityDTO[]> {
    return axiosInstance.get(`${BASE}/priorities`, { params: { ...filter, priorityId: undefined } }).then(r => r.data ?? []);
}

/**
 * Ocupación de la agenda de cada consultorio, con el período anterior. Con tipo y especialidad, pero sin servicio ni
 * código: la capacidad de una agenda no depende del código, y la pantalla elige la agenda en el front. Con Tipo =
 * Servicios devuelve una lista vacía.
 */
export function getServiceStatisticsOccupancy(filter: ServiceStatisticsFilter): Promise<ServiceStatisticsOccupancyDTO[]> {
    return axiosInstance.get(`${BASE}/occupancy`, { params: { ...filter, serviceId: undefined, priorityId: undefined } })
        .then(r => r.data ?? []);
}

/**
 * «Dónde hay más espera»: los servicios y agendas con más espera media, cada uno con su nivel y sus posibles causas, todo
 * calculado en el backend. Sin el filtro de servicio (como la tabla de servicios): la pantalla acota a la agenda elegida.
 * `limit` es cuántas filas trae (el backend usa 5 por defecto); `total` dice cuántas hay en el período.
 */
export function getServiceStatisticsWaitRanking(filter: ServiceStatisticsFilter, limit?: number): Promise<WaitRankingDTO> {
    return axiosInstance.get(`${BASE}/wait-ranking`, { params: { ...filter, serviceId: undefined, limit } })
        .then(r => ({ rows: r.data?.rows ?? [], total: r.data?.total ?? r.data?.rows?.length ?? 0 }));
}

/**
 * Las atenciones que más esperaron en el período, de mayor a menor espera y sin datos del paciente. Mismos filtros que
 * `/services`, con `serviceId` si se quiere solo un servicio o agenda. `limit` va de 1 a 20 (el backend usa 5 por defecto).
 */
export function getServiceStatisticsLongestWaits(filter: ServiceStatisticsFilter, limit?: number): Promise<ServiceStatisticsLongestWaitDTO[]> {
    return axiosInstance.get(`${BASE}/longest-waits`, { params: { ...filter, limit } }).then(r => r.data ?? []);
}

/**
 * Horario programado contra el real de una agenda de consultorio: por día de la semana y por jornada (las 120 más
 * recientes). Mismos filtros que `/services`, más la agenda (`serviceId`, obligatoria). Con una agenda sin horario
 * programado `scheduled` es false y las listas vienen vacías.
 */
export function getServiceStatisticsScheduleHours(filter: ServiceStatisticsFilter, serviceId: number): Promise<ScheduleHoursDTO> {
    return axiosInstance.get(`${BASE}/schedule-hours`, { params: { ...filter, serviceId } }).then(r => ({
        ...r.data,
        toleranceMinutes: r.data.toleranceMinutes ?? 5,
        weekdays: r.data.weekdays ?? [],
        jornadas: r.data.jornadas ?? [],
    }));
}

/**
 * Promedio de la especialidad del profesional en el período, para compararse sin ver a nadie. Siempre es del profesional
 * del usuario (`mine`). Sin `specialityId` el backend usa la más frecuente del profesional (`defaultSpecialityId`).
 */
export function getServiceStatisticsBenchmark(filter: Pick<ServiceStatisticsFilter, 'initialDate' | 'finalDate' | 'specialityId'>):
    Promise<ServiceStatisticsBenchmarkDTO> {
    return axiosInstance.get(`${BASE}/benchmark`, { params: { ...filter, mine: true } }).then(r => ({
        ...r.data,
        professionalSpecialities: r.data.professionalSpecialities ?? [],
        metrics: r.data.metrics ? withMetricDefaults(r.data.metrics) : null,
    }));
}

/**
 * Descarga las estadísticas en PDF o Excel con los mismos filtros que las consultas de la pantalla (el backend vuelve a
 * pedir cada dato con el mismo control de acceso). `filtersText` es el resumen de los filtros aplicados que se imprime en
 * el encabezado; `granularity` es la de la evolución que se ve en pantalla. Devuelve la respuesta completa: el
 * `Content-Disposition` trae el nombre del archivo. Con `responseType: 'blob'` los errores llegan como `Blob`: se leen con
 * `readBlobError` (`services/reportExport.ts`).
 */
export function exportServiceStatistics(filter: ServiceStatisticsFilter, format: ExportFormat, filtersText?: string,
                                        granularity?: ServiceStatisticsGranularity): Promise<AxiosResponse<Blob>> {
    return axiosInstance.get<Blob>(`${BASE}/export`, {
        params: { ...filter, format, filtersText: filtersText || undefined, granularity },
        responseType: 'blob',
    });
}
